import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { isNative } from '../native/platform';
import {
  clearRefreshToken,
  loadRefreshToken,
  persistRefreshToken,
} from '../native/secure-session';
import {
  buildEndSessionUrl,
  driverAuthConfig,
  getAccessToken,
  handleCallback,
  login,
  logout,
  refreshAccessToken,
  setAccessToken,
  TokenRefreshError,
} from './driver-auth';

interface DriverAuthContextValue {
  /** current access token, or null when unauthenticated */
  token: string | null;
  /** true while a native cold-launch biometric-resume is in flight (web: always false) */
  resuming: boolean;
  /** kick off the Keycloak Authorization-Code + PKCE redirect */
  login: () => Promise<void>;
  /** complete the `/callback` round-trip; resolves true on success */
  handleCallback: () => Promise<boolean>;
  /** clear the local token (+ Keystore refresh token) AND end the Keycloak
   *  SSO session (redirects away) — see {@link buildEndSessionUrl} */
  logout: () => void;
  /**
   * F007: a live API call got a 401 (an expired/rejected access token) — on
   * native, try ONE silent Keystore refresh grant (the same body the
   * cold-launch resume uses); otherwise, or on failure, clear the session so
   * `AuthGuard`'s `!token` effect restarts the PKCE redirect. A driver is
   * never stuck on a dead token with no way to force a fresh sign-in.
   */
  recoverExpiredSession: () => void;
}

const DriverAuthContext = createContext<DriverAuthContextValue | null>(null);

export function DriverAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAccessToken());
  const onCallback =
    typeof window !== 'undefined' && window.location.pathname.endsWith('/callback');
  // Only a native cold-launch with no live access token attempts a biometric resume.
  // On web this is false, so the boot effect below never runs and behavior is unchanged.
  const [resuming, setResuming] = useState<boolean>(
    () => isNative() && !getAccessToken() && !onCallback
  );

  // The Keystore refresh-grant body shared by the cold-launch boot resume AND
  // F007's recoverExpiredSession (a live 401 mid-shift): try the stored
  // refresh token → refresh-grant a fresh access token (no full re-login).
  // Returns 'ok' on success; 'no-refresh-token' when none is stored/denied;
  // 'dead-refresh-token' on a GENUINELY dead RT (Keycloak 4xx invalid_grant —
  // the RT IS discarded); 'transient-failure' on a network/5xx hiccup, which
  // PRESERVES the RT so a later attempt can still retry it (an OFFLINE
  // cold-launch — the field driver's primary scenario — must never silently
  // destroy a valid token and force a network-dependent re-login).
  const attemptNativeRefresh = useCallback(async (): Promise<
    'ok' | 'no-refresh-token' | 'dead-refresh-token' | 'transient-failure'
  > => {
    try {
      const rt = await loadRefreshToken(); // biometric-gated; null if none/denied
      if (!rt) return 'no-refresh-token';
      const cfg = driverAuthConfig();
      const { access_token, refresh_token } = await refreshAccessToken(cfg, rt);
      setAccessToken(access_token);
      if (refresh_token) await persistRefreshToken(refresh_token);
      setToken(access_token);
      return 'ok';
    } catch (err) {
      if (err instanceof TokenRefreshError && err.authFailure) {
        await clearRefreshToken();
        return 'dead-refresh-token';
      }
      return 'transient-failure';
    }
  }, []);

  // Biometric-resume boot: a native cold-launch attempts ONE silent refresh via
  // {@link attemptNativeRefresh}; on no-token/deny/failure it falls through to
  // login() (the AuthGuard redirect, not here). Skipped on /callback and on web
  // (resuming starts false there).
  useEffect(() => {
    if (!resuming) return;
    let cancelled = false;
    void attemptNativeRefresh().finally(() => {
      if (!cancelled) setResuming(false);
    });
    return () => {
      cancelled = true;
    };
  }, [resuming, attemptNativeRefresh]);

  // Auth-ready signal: when an access token lands (login-callback OR biometric-resume), tell the
  // outbox to flush any deferred ops (OFF-3 — the outbox subscribes to this; no provider coupling).
  useEffect(() => {
    if (token) window.dispatchEvent(new Event('pipe:driver:auth-ready'));
  }, [token]);

  const doLogin = useCallback(() => login(driverAuthConfig()), []);

  const doHandleCallback = useCallback(async () => {
    const { ok, refreshToken } = await handleCallback(driverAuthConfig());
    if (ok && refreshToken && isNative()) {
      await persistRefreshToken(refreshToken);
    }
    setToken(getAccessToken());
    return ok;
  }, []);

  // F007 (blocker 2): under a live Keycloak SSO cookie, clearing only the
  // local token doesn't end the session — AuthGuard's `!token` effect
  // re-fires login() and Keycloak silently re-authenticates the SAME driver
  // with no prompt (byte-for-byte the Portal's fixed "Sign out does nothing"
  // defect, dccdd0f5). Mirror the Portal/storefront shape: build the
  // RP-initiated end_session URL FIRST (it needs the stored id_token), THEN
  // clear local + Keystore state, and navigate there directly — deliberately
  // NEVER calling setToken(null) here. Clearing React state would re-render
  // AuthGuard and let ITS async window.location.assign win the navigation
  // race against ours. On a shared device this matters for custody
  // attribution: an incomplete sign-out could let the next driver's H1
  // signatures land under the previous driver's `assigned_to` identity.
  const doLogout = useCallback(() => {
    const endSession = buildEndSessionUrl(driverAuthConfig());
    logout();
    void clearRefreshToken();
    window.location.assign(endSession);
  }, []);

  // F007: the DriverErpService 401 hook calls this. Native tries the SAME
  // silent refresh grant as the boot resume first; the access token that just
  // 401'd is confirmed stale either way, so anything but 'ok' clears it —
  // `logout()` only drops the (already-dead) access token + PKCE state, never
  // the Keystore RT (a dead RT was already cleared inside
  // attemptNativeRefresh; a transient hiccup PRESERVES it for a later retry).
  const recoverExpiredSession = useCallback(() => {
    if (!isNative()) {
      logout();
      setToken(null);
      return;
    }
    void attemptNativeRefresh().then(result => {
      if (result !== 'ok') {
        logout();
        setToken(null);
      }
    });
  }, [attemptNativeRefresh]);

  return (
    <DriverAuthContext.Provider
      value={{
        token,
        resuming,
        login: doLogin,
        handleCallback: doHandleCallback,
        logout: doLogout,
        recoverExpiredSession,
      }}
    >
      {children}
    </DriverAuthContext.Provider>
  );
}

export function useDriverAuth(): DriverAuthContextValue {
  const ctx = useContext(DriverAuthContext);
  if (!ctx) {
    throw new Error('useDriverAuth must be used within a DriverAuthProvider');
  }
  return ctx;
}
